import React from 'react';
import {
  Field,
  reduxForm,
  formValueSelector,
  SubmissionError,
} from 'redux-form';
import { connect } from 'react-redux';
import {
  Grid,
  Typography,
  MenuItem,
  FormControlLabel,
  FormLabel
} from '@material-ui/core';
import styled from 'styled-components';
import {
  FormTextField,
  FormCheckbox,
  FormError
} from '../../../../components/Form/index';
import {
  OfficePhoneLabel,
  AdditionalFlooringForms,
  StepControl,
  FormWrapper,
  FieldWrapper,
  StepperTitle,
  FormFileField
} from '../components';
import { maxLength, required, validateDoc} from '../../../../utils/validators';
import { FLOORING_FORM_COMPANIES, REGISTRATION_MAX_STRING_LENGTH, SIGNUP_ACH_METHODS } from '../../../../constants';
import scrollToError from '../../../../utils/scrollToError';
import AchMethodPicker from "../components/AchMethodPicker";
import { modalsToggle } from "../../../../actions";

const maxStringLength = maxLength(REGISTRATION_MAX_STRING_LENGTH);

const renderBasicFields = fields =>
  fields.map(({ name = '', label = '', sm = 12, validation = [required, maxStringLength], ...rest }, index) => (
    <FieldWrapper sm={sm} key={index}>
      <Field
        name={name}
        component={FormTextField}
        label={label}
        fullWidth
        validate={validation}
        {...rest}
      />
    </FieldWrapper>
  ));

const achFormFields = [
  {
    name: 'ach_form.full_account_name',
    label: 'Full account name'
  },
  {
    name: 'ach_form.bank_account',
    label: 'Bank account #'
  },
  {
    name: 'ach_form.bank_routing',
    label: 'Bank routing #',
    sm: 6
  },
  {
    name: 'ach_form.bank_name',
    label: 'Bank name',
    sm: 6
  },
];

const flooringFormFields = [
  {
    name: 'flooring_form.name_act',
    label: 'Name on Acct'
  },
  {
    name: 'flooring_form.act',
    label: 'Acct #'
  },
  {
    name: 'flooring_form.auction_access',
    label: 'Auction Access #',
    validation: maxStringLength
  }
];

const checkBoxes = [
  {
    name: 'purchase_method.cash',
    label: 'Cash'
  },
  {
    name: 'purchase_method.company_check',
    label: 'Company Check'
  },
  {
    name: 'purchase_method.cashiers_check',
    label: "Cashier's check"
  },
  {
    name: 'purchase_method.ach',
    label: 'ACH'
  },
  {
    name: 'purchase_method.flooring',
    label: 'Flooring'
  }
];

const renderCheckboxes = (array, disabledNames = []) =>
  array.map(({ label, name }, index) => (
    <FormControlLabel
      control={<Field name={name} component={FormCheckbox} color="primary" disabled={disabledNames.includes(name)} />}
      label={label}
      key={index}
    />
  ));

const StyledFlooringFormLabel = styled(FormLabel)`
  margin-top: 20px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.25px;
  color: #222222;
`;

const StyledACHFormLabel = styled(FormLabel)`
  margin-top: 10px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.25px;
  color: #222222;
`;

const StyledDisclosure = styled(Typography)`
  margin-top: 10px;
  margin-bottom: 5px;
`;

const StyledDisclaimerDescription = styled(Typography)`
  color: #616161;
`;

const submitValidation = (onSubmit, buyFeeMethod) => ({
  purchase_method,
  ach_form,
  ach_method,
  ach_voided_check_image,
  flooring_form
}) => {
  const isPurchaseOptionChosen = Object.values(purchase_method || {}).filter(
    i => i
  ).length;

  if (!isPurchaseOptionChosen) {
    // eslint-disable-next-line no-underscore-dangle
    throw new SubmissionError({
      _error: 'At least one purchase method should be chosen'
    });
  }

  const isAchOrFlooringSelected = purchase_method && (purchase_method.flooring || purchase_method.ach)
  if (buyFeeMethod === 'ach_flooring' && !isAchOrFlooringSelected) {
    throw new SubmissionError({
      _error: 'At least one of the ACH or Flooring purchase methods should be chosen'
    });
  }

  const result = {
    purchase_method
  };
  if (ach_method) {
    result.ach_method = ach_method;
  }
  if (ach_form && ach_method === SIGNUP_ACH_METHODS.ENTER_ACH_INFORMATION) {
    result.ach_form = ach_form;
  }
  if (ach_voided_check_image && ach_method === SIGNUP_ACH_METHODS.UPLOAD_VOIDED_CHECK) {
    result.ach_voided_check_image = ach_voided_check_image;
  }
  if (flooring_form) {
    result.flooring_form = flooring_form;
  }
  return onSubmit(result);
};

const fifthBuyerStep = ({
  handleSubmit,
  onSubmit,
  submitting,
  prevStep,
  purchase_method: paymentMethod,
  flooring_form: flooringForm,
  buy_fee_method: buyFeeMethod,
  ach_method: achMethod,
  toggleModal,
  error
}) => (
  <form onSubmit={handleSubmit(submitValidation(onSubmit, buyFeeMethod))}>
    <FormWrapper>
      <StepperTitle title="Purchase Method" subTitle="Step 5 of 5" />
      <Grid container spacing={24}>
        <FieldWrapper>
          <StyledDisclaimerDescription>
            We highly recommend you choosing as many purchase methods as
            possible - the more methods you choose, the more vehicle options you
            will have.
          </StyledDisclaimerDescription>
        </FieldWrapper>
        <FieldWrapper>
          <FormLabel component="legend">Purchase Method</FormLabel>
          {renderCheckboxes(checkBoxes)}
        </FieldWrapper>
        {paymentMethod && paymentMethod.ach && (
          <>
            <FieldWrapper>
              <StyledACHFormLabel component="legend">
                ACH Form
              </StyledACHFormLabel>

              <AchMethodPicker />

              {achMethod === SIGNUP_ACH_METHODS.ENTER_ACH_INFORMATION && (
                <StyledDisclosure>
                  By filling out my (our) banking and flooring information below
                  {' '}
                  I (we) have authority to and authorize AutoAxess to Electronically debit
                  {' '}
                  my (our) account (and if necessary, electronically credit my (our) account to correct erroneous debits) as follows:
                </StyledDisclosure>
              )}

              {achMethod === SIGNUP_ACH_METHODS.UPLOAD_VOIDED_CHECK && (
                <StyledDisclosure>
                  By uploading a voided check and entering my flooring information below
                  {' '}
                  I (we) have authority to and authorize AutoAxess to Electronically debit
                  {' '}
                  my (our) account (and if necessary, electronically credit my (our) account to correct erroneous debits) as follows:
                </StyledDisclosure>
              )}

              {achMethod === SIGNUP_ACH_METHODS.UPLOAD_VOIDED_CHECK && (
                <FieldWrapper>
                  <Field
                    name="ach_voided_check_image"
                    component={FormFileField}
                    label=""
                    buttonText="Upload Voided Check"
                    id="ach_voided_check_image"
                    type="file"
                    validate={[required, validateDoc]}
                    formName="registrationForm"
                  />
                  <FormLabel style={{position: 'relative', left: '200px', bottom: '25px', color: 'red', display: 'none'}}>
                    (This is required)
                  </FormLabel>
                </FieldWrapper>
              )}

              {achMethod === SIGNUP_ACH_METHODS.ENTER_ACH_INFORMATION && (
                <Field
                  name="ach_form.account_type"
                  component={FormTextField}
                  label="Select Account Type"
                  fullWidth
                  validate={required}
                  select
                >
                  <MenuItem value="checking">Checking</MenuItem>
                  <MenuItem value="savings">Savings</MenuItem>
                </Field>
              )}

            </FieldWrapper>

            {achMethod === SIGNUP_ACH_METHODS.ENTER_ACH_INFORMATION &&
              renderBasicFields(achFormFields)
            }
          </>
        )}
        {paymentMethod && paymentMethod.flooring && (
          <>
            <FieldWrapper>
              <StyledFlooringFormLabel component="legend">
                Flooring Form
              </StyledFlooringFormLabel>
              <Field
                name="flooring_form.flooring_company"
                component={FormTextField}
                label="Select Flooring Company"
                fullWidth
                validate={required}
                select
              >
                {FLOORING_FORM_COMPANIES.map((company) => (
                  <MenuItem value={company.value}>{company.name}</MenuItem>
                ))}
              </Field>
            </FieldWrapper>
            {renderBasicFields(flooringFormFields)}
            <FieldWrapper>
              <AdditionalFlooringForms
                auction_access={flooringForm && flooringForm.auction_access ? flooringForm.auction_access : ''}
                name_act={flooringForm && flooringForm.name_act ? flooringForm.name_act : ''}
              />
            </FieldWrapper>
          </>
        )}
        {error && <FormError msg={error} />}
        {paymentMethod && (paymentMethod.ach || paymentMethod.flooring) && (
          <FieldWrapper>
            <StyledDisclosure>
              I (we) understand that this authorization will remain in full force and effect until
              {' '}
              I (we) notify AutoAxess in writing or by phone that I (we) wish to revoke this authorization.
              {' '}
              I (we) understand that AutoAxess requires at least 30 days notice to cancel this authorization.
            </StyledDisclosure>
          </FieldWrapper>
        )}
        <FieldWrapper>
          <StyledDisclosure>
            By clicking on the Finish button, you agree to our {' '}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#terms" onClick={() => toggleModal('termsAndConditionsModal')}>
              Terms & Conditions
            </a>.
          </StyledDisclosure>
        </FieldWrapper>
      </Grid>
    </FormWrapper>
    <OfficePhoneLabel />
    <StepControl prevStep={prevStep} isLoading={submitting} nextLabelText="Finish" />
  </form>
);

const selector = formValueSelector('registrationForm');

const form = reduxForm({
  form: 'registrationForm',
  fields: [
    'dealership_name',
    // 'username',
    'first_name',
    'last_name',
    'email',
    'password',
    'confirm_password',
    'role',
    'street',
    'city',
    'state',
    'zip',
    'phone_number',
    'store_number',
    'secondary_contact_name',
    'secondary_contact_phone',
    'dealer_license_number',
    'dealer_license_expiry_date',
    'dealer_bond',
    'dmv_license',
    'resale_certificate',
    'city_business_license',
    'credit_card_number',
    'credit_card_expiry_date',
    'credit_card_cvc',
    'purchase_method',
    'ach_method',
    'ach_voided_check_image',
    'ach_form',
    'flooring_form'
  ],
  onSubmitFail: scrollToError,
  destroyOnUnmount: false,
})(fifthBuyerStep);

const mapDispatchToProps = dispatch => ({
  toggleModal: key => dispatch(modalsToggle(key)),
});

export default connect(state => ({
  purchase_method: selector(state, 'purchase_method'),
  flooring_form: selector(state, 'flooring_form'),
  buy_fee_method: selector(state, 'buy_fee_method'),
  ach_method: selector(state, 'ach_method'),
}), mapDispatchToProps)(form);
